import {
	ComponentLayout,
	FlexContainerLayouts,
	JustifyContent,
	Alignment,
	Size,
	Calculation,
	UnitSize,
} from '@wix/thunderbolt-becky-types'

import { FlatListProps, FlexAlignType, FlexStyle } from '@wix/react-native-style-types'

type RepeaterType = 'slider' | 'list' | 'cards' | undefined
type RNJustifyContent = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'

// Map CSS alignment values to React Native
export const reactNativeAlignItemsMap: Record<Alignment, FlexAlignType> = {
	auto: 'stretch',
	stretch: 'stretch',
	start: 'flex-start',
	center: 'center',
	end: 'flex-end',
}

export const reactNativeJustifyContentMap: Record<JustifyContent, RNJustifyContent> = {
	start: 'flex-start',
	center: 'center',
	end: 'flex-end',
	spaceBetween: 'space-between',
	spaceAround: 'space-around',
	spaceEvenly: 'space-evenly',
}

const isCalculationSize = (u: Size): u is Calculation => u.type === 'Calc'
const isPercentageSize = (u: Size): u is UnitSize => u.type === 'percentage'

function calculateRelativePart(percentage: number): number {
	return Math.round(100 / percentage)
}
// Studio Logic - responsive-editor-packages/packages/editor-components-repeater/src/calculations/repeaterItem/repeaterItemCalculations.ts#L49
const getPercentageFromCalcString = (size: Calculation): number | undefined => {
	const percentageSlicedWidth = size.value.split('(').join('')
	const percentageWidth = percentageSlicedWidth.split('%')[0]
	const convertedPercentageWidth = +percentageWidth
	return isNaN(convertedPercentageWidth) ? undefined : convertedPercentageWidth
}

function getPercentageValue(width: Size): number | undefined {
	if (isCalculationSize(width)) {
		return getPercentageFromCalcString(width)
	} else if (isPercentageSize(width)) {
		return width.value
	}
	return undefined
}

export function getItemsPerRow(childComponentLayout: ComponentLayout): number | undefined {
	if (!childComponentLayout?.width) {
		return undefined
	}
	const percentageValue = getPercentageValue(childComponentLayout.width)
	return percentageValue ? calculateRelativePart(percentageValue) : undefined
}

export function getRepeaterType(flexContainerLayout: FlexContainerLayouts): RepeaterType {
	if (flexContainerLayout) {
		const { wrap, overflowX, overflowY } = flexContainerLayout
		if (wrap === 'nowrap') {
			if (overflowX === 'scroll' || overflowY === 'scroll') {
				return 'slider'
			} else {
				return 'list'
			}
		} else {
			return 'cards'
		}
	}
}

function isFlexContainerLayout(flexContainerLayout: any): flexContainerLayout is FlexContainerLayouts {
	return (
		typeof flexContainerLayout === 'object' &&
		flexContainerLayout !== null &&
		'direction' in flexContainerLayout &&
		(flexContainerLayout.direction === 'row' ||
			flexContainerLayout.direction === 'column' ||
			flexContainerLayout.direction === 'rowReverse' ||
			flexContainerLayout.direction === 'columnReverse')
	)
}

export function getRNRepeaterFlatListProps(
	repeaterType: RepeaterType,
	flexContainerLayout: FlexContainerLayouts,
	itemsPerRow = 3
): Partial<FlatListProps<any>> | undefined {
	if (isFlexContainerLayout(flexContainerLayout)) {
		const {
			direction: layoutDirection = 'row',
			alignItems,
			justifyContent,
			rowGap = { value: 0 },
			columnGap = { value: 0 },
			padding: { top = { value: 0 }, right = { value: 0 }, bottom = { value: 0 }, left = { value: 0 } } = {},
		} = flexContainerLayout

		const isReverse = layoutDirection === 'rowReverse' || layoutDirection === 'columnReverse'
		const direction: FlexStyle['direction'] = isReverse ? 'rtl' : 'ltr'
		const isSlider = repeaterType === 'slider'
		const isCards = repeaterType === 'cards'

		const contentContainerStyle = {
			direction,
			alignItems: alignItems && reactNativeAlignItemsMap[alignItems as Alignment],
			justifyContent: justifyContent && reactNativeJustifyContentMap[justifyContent as JustifyContent],
			gap: isSlider ? (columnGap as UnitSize).value : (rowGap as UnitSize).value,
			paddingTop: (top as UnitSize).value,
			paddingRight: (right as UnitSize).value,
			paddingBottom: (bottom as UnitSize).value,
			paddingLeft: (left as UnitSize).value,
		}

		const columnWrapperStyle =
			isCards && itemsPerRow > 1
				? {
					alignItems: alignItems && reactNativeAlignItemsMap[alignItems as Alignment],
					justifyContent:
							justifyContent && reactNativeJustifyContentMap[justifyContent as JustifyContent],
					gap: (columnGap as UnitSize).value,
				}
				: undefined

		return {
			horizontal: isSlider,
			numColumns: isCards ? itemsPerRow : undefined,
			showsHorizontalScrollIndicator: false,
			contentContainerStyle,
			columnWrapperStyle,
		}
	}
}
